import type { GameEngine } from '@core/GameEngine';
import type { PlayerId } from '@/types/player.types';
import type { BoardPosition } from '@/types/warrior.types';
import {
  type NetworkMessage,
  type NetworkRole,
  type NetworkState,
  getLocalPlayerId,
  getRemotePlayerId,
} from './NetworkTypes';
export type { NetworkRole } from './NetworkTypes';
import { getPeerManager, resetPeerManager } from './PeerManager';

/**
 * Callbacks for network events
 */
export interface NetworkControllerCallbacks {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
  onRemoteTeamReady?: (playerName: string, warriorIds: string[], rating: number) => void;
  onCoinFlipResult?: (startingPlayer: PlayerId) => void;
  onRemoteWarriorSelected?: (warriorInstanceId: string) => void;
  onRemoteMove?: (to: BoardPosition) => void;
  onRemoteDash?: (to: BoardPosition) => void;
  onRemoteAttack?: () => void;
  onRemoteHeal?: () => void;
  onRematchRequest?: () => void;
}

/**
 * NetworkController - Manages game synchronization over PeerJS
 *
 * This controller sits between the UI and GameEngine, intercepting
 * local actions to send to the peer and receiving remote actions
 * to execute on the local engine.
 */
export class NetworkController {
  private engine: GameEngine;
  private role: NetworkRole;
  private callbacks: NetworkControllerCallbacks;
  private peerManager = getPeerManager();

  constructor(engine: GameEngine, role: NetworkRole, callbacks: NetworkControllerCallbacks = {}) {
    this.engine = engine;
    this.role = role;
    this.callbacks = callbacks;

    // Set up message handler
    this.peerManager.setHandlers({
      onConnected: () => {
        console.log('[NetworkController] Connected');
        this.callbacks.onConnected?.();
      },
      onDisconnected: () => {
        console.log('[NetworkController] Disconnected');
        this.callbacks.onDisconnected?.();
      },
      onError: (error) => {
        console.error('[NetworkController] Error:', error);
        this.callbacks.onError?.(error);
      },
      onMessage: (message) => {
        this.handleMessage(message);
      },
    });
  }

  /**
   * Get the local player ID based on role
   */
  getLocalPlayerId(): PlayerId {
    return getLocalPlayerId(this.role);
  }

  /**
   * Get the remote player ID based on role
   */
  getRemotePlayerId(): PlayerId {
    return getRemotePlayerId(this.role);
  }

  /**
   * Check if it's the local player's turn
   */
  isLocalTurn(): boolean {
    return this.engine.getState().currentTurn === this.getLocalPlayerId();
  }

  /**
   * Check if connected to peer
   */
  isConnected(): boolean {
    return this.peerManager.isConnected();
  }

  /**
   * Handle incoming network message
   */
  private handleMessage(message: NetworkMessage): void {
    console.log('[NetworkController] Received:', message.type);

    switch (message.type) {
      case 'TEAM_READY':
        this.callbacks.onRemoteTeamReady?.(
          message.playerName,
          message.warriorIds,
          message.rating
        );
        break;

      case 'COIN_FLIP_RESULT':
        this.callbacks.onCoinFlipResult?.(message.startingPlayer);
        break;

      case 'SELECT_WARRIOR':
        this.callbacks.onRemoteWarriorSelected?.(message.warriorInstanceId);
        break;

      case 'EXECUTE_MOVE':
        this.callbacks.onRemoteMove?.(message.to);
        break;

      case 'EXECUTE_DASH':
        this.callbacks.onRemoteDash?.(message.to);
        break;

      case 'EXECUTE_ATTACK':
        this.callbacks.onRemoteAttack?.();
        break;

      case 'EXECUTE_HEAL':
        this.callbacks.onRemoteHeal?.();
        break;

      case 'REMATCH_REQUEST':
        this.callbacks.onRematchRequest?.();
        break;

      case 'DISCONNECT':
        this.callbacks.onDisconnected?.();
        break;
    }
  }

  /**
   * Send local team ready message
   */
  sendTeamReady(playerName: string, warriorIds: string[], rating: number): void {
    this.peerManager.send({
      type: 'TEAM_READY',
      playerName,
      warriorIds,
      rating,
    });
  }

  /**
   * Send coin flip result (host only)
   */
  sendCoinFlipResult(startingPlayer: PlayerId): void {
    if (this.role !== 'host') {
      console.warn('[NetworkController] Only host can send coin flip result');
      return;
    }
    this.peerManager.send({
      type: 'COIN_FLIP_RESULT',
      startingPlayer,
    });
  }

  /**
   * Send warrior selection
   */
  sendSelectWarrior(warriorInstanceId: string): void {
    this.peerManager.send({
      type: 'SELECT_WARRIOR',
      warriorInstanceId,
    });
  }

  /**
   * Send move action
   */
  sendMove(to: BoardPosition): void {
    this.peerManager.send({
      type: 'EXECUTE_MOVE',
      to,
    });
  }

  /**
   * Send dash action
   */
  sendDash(to: BoardPosition): void {
    this.peerManager.send({
      type: 'EXECUTE_DASH',
      to,
    });
  }

  /**
   * Send attack action
   */
  sendAttack(): void {
    this.peerManager.send({
      type: 'EXECUTE_ATTACK',
    });
  }

  /**
   * Send heal action
   */
  sendHeal(): void {
    this.peerManager.send({
      type: 'EXECUTE_HEAL',
    });
  }

  /**
   * Send rematch request
   */
  sendRematchRequest(): void {
    this.peerManager.send({
      type: 'REMATCH_REQUEST',
    });
  }

  /**
   * Send disconnect message and clean up
   */
  disconnect(): void {
    this.peerManager.send({
      type: 'DISCONNECT',
    });
    resetPeerManager();
  }

  /**
   * Get current network state
   */
  getNetworkState(): NetworkState {
    return {
      role: this.role,
      roomId: this.peerManager.roomId,
      connectionStatus: this.peerManager.status,
      remotePlayerReady: false, // Tracked by scene
    };
  }
}

// Singleton instance
let instance: NetworkController | null = null;

export function createNetworkController(
  engine: GameEngine,
  role: NetworkRole,
  callbacks: NetworkControllerCallbacks = {}
): NetworkController {
  if (instance) {
    instance.disconnect();
  }
  instance = new NetworkController(engine, role, callbacks);
  return instance;
}

export function getNetworkController(): NetworkController | null {
  return instance;
}

export function clearNetworkController(): void {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
}
